/**
 * The frame both unauthenticated routes sit in.
 *
 * IT IS NOT THE CABINET'S AppShell. `/sign-in` and `/enter` are the only two unauthenticated
 * routes (adr/0007-web-app-stack.md), and a nav rail on a page where every destination refuses
 * you is worse than no nav rail. So this is one centred card and nothing else.
 *
 * THE HELP LINK LEAVES THE CABINET, AND THAT IS RULED. LOCKED erratum G28 puts the program pages,
 * the FAQ and the terms on moonx.app: "the cabinet links to them and hosts none"
 * (docs/design/00-scope-v1.md, repeated at 09-authors-app.md). The support ADDRESSES
 * cannot be shown here either — `getPlatformContacts` sits behind the bearer scheme, so an
 * unauthenticated visitor cannot read them.
 *
 * ONE `<h1>` PER PAGE. The card's title is it, so nothing inside a panel may use `Title order={1}`.
 *
 * ★ DRAWN AS THE PROTOTYPE'S SIGN-IN DIALOG (the owner's live look at real cabinets, 2026-10-01 /
 * design 15): the card over the field of stars, the MoonX mark at its head - white, drawn dark in
 * the light scheme - the title at the dialog's size, and the help line under a hairline at its
 * foot, where the prototype puts its quiet line. Everything is inside the card and the card is
 * inside the router's `main` (`outsideTheShell`, src/router.tsx): one landmark, every word in it.
 * The not-found draws the same card without the help line (`withHelp`), because its two ways on are
 * the only links it may offer (test/routes/outside-the-cabinet.spec.tsx). The measurements are in
 * ./auth-shell.module.css.
 */
import { Anchor } from '@mantine/core';
import { CircleHelp } from '@moonx/ui';
import type { ReactNode } from 'react';
import logoUrl from '../../../assets/moonx-logo.png';
import { Trans, useTranslation } from '../../../i18n';
import { WEBSITE_ORIGIN } from '../handoff/website-handoff';
import classes from './auth-shell.module.css';

export interface AuthShellProps {
  readonly title: string;
  /** One sentence under the title. Optional: the hand-off screen is a transit screen and says less. */
  readonly standfirst?: string;
  readonly children: ReactNode;
  /** The "Trouble signing in?" line at the foot. On by default; the not-found leaves it out. */
  readonly withHelp?: boolean;
}

export function AuthShell({ title, standfirst, children, withHelp = true }: AuthShellProps) {
  const { t } = useTranslation('auth');
  return (
    <div className={classes.page}>
      <div className={classes.sky} aria-hidden="true" />
      <div className={classes.card}>
        <div className={classes.head}>
          <img src={logoUrl} alt="MoonX" className={classes.logo} />
          <h1 className={classes.title}>{title}</h1>
          {standfirst === undefined ? null : <p className={classes.standfirst}>{standfirst}</p>}
        </div>

        {children}

        {withHelp ? (
          <p className={classes.foot}>
            <CircleHelp size={13} aria-hidden="true" />
            <span>
              <Trans
                t={t}
                i18nKey="shell.help"
                components={{ anchor: <Anchor href={WEBSITE_ORIGIN} size="xs" rel="noopener" /> }}
              />
            </span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
